import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import SearchBar from "components/SearchBar";
import TableComponent from "components/TableComponent";
import { showError, showToast } from "helpers";
import MainContainer from "layout/MainContainer";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useLazyGetAllInvestmentQuery,
  useUpdateInvestmentPlanMutation,
} from "service/investment";
import { CommonBody } from "types/General";
import { generateEncryptedKeyBody } from "utils/crypto";
import { isValidInput } from "utils/validations";

const InvestmentPlans = () => {
  const navigate = useNavigate();
  const [getInvestmentPlans] = useLazyGetAllInvestmentQuery();
  const [updateInvestmentPlan] = useUpdateInvestmentPlanMutation();

  const label = { inputProps: { "aria-label": "Switch demo" } };

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [investmentData, setInvestmentData] = useState<any[]>([]);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleStatusToggle = async (id: string, status: boolean) => {
    const body = {
      isActive: status,
    };
    console.log(body);
    try {
      const encryptedBody = generateEncryptedKeyBody(body);
      if (!encryptedBody) {
        showError("Encryption failed. Please try again.");
        return;
      }
      const response = await updateInvestmentPlan({
        body: encryptedBody as CommonBody,
        id,
      }).unwrap();
      if (response?.statusCode === 200) {
        const updatePlan = investmentData?.map((item: any) => {
          if (item?._id === id) {
            return {
              ...item,
              isActive: status,
            };
          }
          return item;
        });
        setInvestmentData(updatePlan);
        showToast(
          status ? "Activated successfully" : "Deactivated successfully"
        );
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const columns: GridColDef[] = [
    {
      flex: 1,
      minWidth: 100,
      field: "name",
      headerName: "Plan Name",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.name || ""}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 100,
      field: "min",
      headerName: "Minimum Investment",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{`AED ${row?.minimumInvestment}`}</p>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 100,
      field: "roi",
      headerName: "ROI%",
      editable: false,
      sortable: false,

      renderCell: ({ row }) => {
        return (
          <div className="user_block">
            <p>{row?.expectedReturn || "0"}%</p>
          </div>
        );
      },
    },
    // {
    //   flex: 1,
    //   minWidth: 100,
    //   field: "activeInvestors",
    //   headerName: "Active Investors",
    //   editable: false,
    //   sortable: false,

    //   renderCell: ({ row }) => {
    //     return (
    //       <div className="user_block">
    //         <p>12%</p>
    //       </div>
    //     );
    //   },
    // },
    {
      flex: 1,
      minWidth: 150,
      field: "status",
      filterable: false,
      sortable: false,
      headerName: "Status",
      renderCell: ({ row }) => {
        return (
          <Switch
            {...label}
            size="small"
            checked={row?.isActive}
            onChange={() => handleStatusToggle(row?._id, !row?.isActive)}
          />
        );
      },
    },

    {
      minWidth: 130,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="table_actions v2">
          {/* <Box>
            <IconButton>
              <img src="/static/images/eye_icon.svg" alt="" />
            </IconButton>
          </Box> */}
          <Box>
            <IconButton
              onClick={() =>
                navigate(
                  `/manage-investments/investment-plans/edit/${row?._id}`
                )
              }
            >
              <img src="/static/images/edit2_icon.svg" alt="" />
            </IconButton>
          </Box>
          {/* <Box>
            <IconButton>
              <img src="/static/images/trash2_icon.svg" alt="" />
            </IconButton>
          </Box> */}
        </div>
      ),
    },
  ];

  const getInvestmentApi = async () => {
    setLoading(true);
    try {
      const response = await getInvestmentPlans({
        page: page,
        search: debouncedSearchTerm,
      }).unwrap();
      setLoading(false);
      if (response.statusCode === 200) {
        setInvestmentData(response?.data?.list || []);
        setCount(response?.data?.totalCount);
        setPage(response?.data?.currentPage);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getInvestmentApi();
  }, [page]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Investment Plans</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -
          Investment Plans
        </p>
      </div>
      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
            <div className="right_s">
              <Box>
                <Button
                  className="btn btn_orange"
                  onClick={() =>
                    navigate("/manage-investments/investment-plans/add")
                  }
                >
                  Add Investment Plan
                </Button>
              </Box>
            </div>
          </div>

          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={investmentData}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
        </Box>
      </div>

      <Grid mt={2} container spacing={3}>
        {investmentData?.map((plan: any) => (
          <Grid item xs={12} sm={6} md={4} key={plan._id}>
            <Card sx={{ opacity: plan.isActive ? 1 : 0.6 }}>
              <CardHeader
                title={
                  <Typography variant="h6" fontWeight={600}>
                    {plan.name}
                  </Typography>
                }
                action={
                  <Chip
                    label={plan.isActive ? "Active" : "Inactive"}
                    color={plan.isActive ? "success" : "default"}
                    size="small"
                  />
                }
              />
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Minimum Investment
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  ${plan.minimumInvestment.toLocaleString()}
                </Typography>

                <Typography variant="body2" color="text.secondary" mt={2}>
                  Monthly ROI
                </Typography>
                <Typography variant="subtitle1" color="green" fontWeight={600}>
                  {plan.expectedReturn}%
                </Typography>

                {/* <Typography variant="body2" color="text.secondary" mt={2}>
                  Active Investors
                </Typography>
                <Typography>{plan.activeInvestors}</Typography> */}

                <Typography variant="body2" color="text.secondary" mt={2}>
                  Description
                </Typography>
                <Typography variant="body2">{plan.description}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </MainContainer>
  );
};

export default InvestmentPlans;
